<template>
  <v-container fluid class="tc-page pa-6">
    <transition name="monitor-intro">
      <div v-if="showIntroSplash" class="tc-intro-overlay">
        <div class="tc-intro-burst"></div>
        <div class="tc-intro-ring tc-intro-ring--outer"></div>
        <div class="tc-intro-ring tc-intro-ring--inner"></div>
        <div class="tc-intro-icon-wrap">
          <v-avatar color="#0e7490" size="88" class="tc-intro-icon" rounded>
            <v-icon dark size="46">mdi-chart-timeline-variant</v-icon>
          </v-avatar>
        </div>
      </div>
    </transition>

    <section class="tc-shell">
      <header class="tc-hero">
        <v-avatar color="#0e7490" size="58" class="tc-hero-icon" rounded>
          <v-icon dark size="30">mdi-chart-timeline-variant</v-icon>
        </v-avatar>
        <div class="tc-hero-copy">
          <div class="tc-eyebrow">GRAFICAS SENSORES</div>
          <h1>Tiempo ciclo</h1>
          <p>Monitoreo ligero desde Postgres/Timescale con la misma vibra premium del reporte de sensores. En vivo usa 5 minutos; historico carga solo un dia.</p>
        </div>
        <div class="tc-hero-sparks" aria-hidden="true">
          <span class="tc-hero-spark tc-hero-spark--live">
            <v-icon>mdi-broadcast</v-icon>
          </span>
          <span class="tc-hero-spark tc-hero-spark--chart">
            <v-icon>mdi-pulse</v-icon>
          </span>
          <span class="tc-hero-spark tc-hero-spark--day">
            <v-icon>mdi-calendar-search</v-icon>
          </span>
        </div>
      </header>

      <section class="tc-control-panel">
        <div class="tc-mode-row">
          <v-btn-toggle v-model="modoConsulta" mandatory class="tc-mode-toggle" @change="onModoChange">
            <v-btn value="live">
              <v-icon left small class="tc-mode-icon tc-mode-icon--live">mdi-broadcast</v-icon>
              En vivo
            </v-btn>
            <v-btn value="day">
              <v-icon left small class="tc-mode-icon tc-mode-icon--day">mdi-calendar-search</v-icon>
              Consulta por dia
            </v-btn>
          </v-btn-toggle>
          <div class="tc-mode-note">
            <v-icon small color="#0e7490">mdi-shield-check-outline</v-icon>
            {{ modoConsulta === 'live' ? 'Ventana fija de 5 minutos con polling controlado.' : 'Carga unica de 00:00 a 23:59 del dia seleccionado.' }}
          </div>
        </div>

        <v-row dense align="center">
          <v-col cols="12" md="3">
            <v-select
              v-model="filtros.area"
              :items="areas"
              item-text="nombre"
              item-value="idArea"
              label="Area"
              outlined
              dense
              class="tc-field"
              prepend-inner-icon="mdi-domain"
              hide-details="auto"
              clearable
              @change="onAreaChange"
            />
          </v-col>

          <v-col cols="12" md="4">
            <v-autocomplete
              v-model="filtros.instancia"
              :items="instanciasFiltradas"
              :item-text="textoMaquina"
              item-value="idMaquinaIns"
              label="Maquina"
              outlined
              dense
              class="tc-field"
              prepend-inner-icon="mdi-robot-industrial"
              hide-details="auto"
              clearable
              :loading="loadingInstancias"
              @change="onMaquinaChange"
            >
              <template v-slot:item="{ item }">
                <v-list-item-content>
                  <v-list-item-title>{{ textoMaquina(item) }}</v-list-item-title>
                  <v-list-item-subtitle>{{ item.nombre || 'Sin nombre' }}</v-list-item-subtitle>
                </v-list-item-content>
              </template>
            </v-autocomplete>
          </v-col>

          <v-col cols="12" md="3">
            <div class="tc-token-pill" :class="{ 'tc-token-pill--empty': !tokenActual }">
              <v-icon :color="tokenActual ? '#0e7490' : '#94a3b8'">mdi-key-variant</v-icon>
              <div>
                <span>Token TC detectado</span>
                <strong>{{ tokenActual || (loadingClaves ? 'Buscando...' : 'Selecciona maquina') }}</strong>
              </div>
              <v-progress-circular v-if="loadingClaves" indeterminate color="#0e7490" size="18" width="2" />
            </div>
          </v-col>

          <v-col cols="12" md="2">
            <v-menu
              v-if="modoConsulta === 'day'"
              v-model="fechaMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              content-class="tc-date-menu"
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <button type="button" class="tc-date-trigger" v-bind="attrs" v-on="on">
                  <span class="tc-date-trigger__icon">
                    <v-icon>mdi-calendar-star</v-icon>
                  </span>
                  <span class="tc-date-trigger__copy">
                    <span>Fecha</span>
                    <strong>{{ filtros.fecha }}</strong>
                  </span>
                  <v-icon class="tc-date-trigger__chevron">mdi-chevron-down</v-icon>
                </button>
              </template>
              <v-date-picker
                v-model="filtros.fecha"
                color="#0e7490"
                header-color="#0e7490"
                class="tc-date-picker"
                no-title
                @input="fechaMenu = false"
              />
            </v-menu>

            <div v-else class="tc-live-window">
              <span>Ventana</span>
              <strong>En vivo · 5 min</strong>
            </div>
          </v-col>
        </v-row>

        <div class="tc-action-row">
          <div class="tc-primary-actions">
            <v-btn
              v-if="modoConsulta === 'live'"
              depressed
              large
              class="tc-action-btn"
              :color="monitoreando ? 'error' : 'cyan darken-3'"
              :disabled="!puedeConsultar"
              @click="toggleMonitoreo"
            >
              <v-icon left>{{ monitoreando ? 'mdi-stop-circle' : 'mdi-play-circle' }}</v-icon>
              {{ monitoreando ? 'Detener vivo' : 'Iniciar vivo' }}
            </v-btn>

            <v-btn
              v-else
              depressed
              large
              color="cyan darken-3"
              class="tc-action-btn"
              :loading="consultando"
              :disabled="!puedeConsultar"
              @click="consultarDia"
            >
              <v-icon left>mdi-calendar-clock</v-icon>
              Consultar dia
            </v-btn>

            <v-btn depressed class="tc-clean-btn" :disabled="!puntos.length" @click="limpiarDatos">
              <v-icon left>mdi-broom-outline</v-icon>
              Limpiar
            </v-btn>
          </div>

          <v-menu v-if="modoConsulta === 'live'" offset-y>
            <template v-slot:activator="{ on, attrs }">
              <v-btn depressed class="tc-interval-btn" v-bind="attrs" v-on="on">
                <v-icon left>mdi-timer-outline</v-icon>
                Cada {{ intervaloConsulta / 1000 }}s
              </v-btn>
            </template>
            <v-list dense>
              <v-list-item v-for="opcion in opcionesIntervalo" :key="opcion.valor" @click="setIntervalo(opcion.valor)">
                <v-list-item-title>{{ opcion.label }}</v-list-item-title>
                <v-list-item-action v-if="intervaloConsulta === opcion.valor">
                  <v-icon small color="primary">mdi-check</v-icon>
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
      </section>

      <v-row dense class="mb-5">
        <v-col cols="6" md="3" v-for="item in tarjetas" :key="item.label">
          <div
            class="tc-stat"
            :style="{ '--stat-color': item.color, '--stat-soft': item.soft }"
          >
            <div class="tc-stat__glow"></div>
            <div class="tc-stat__top">
              <span class="tc-stat__icon">
                <v-icon>{{ item.icon }}</v-icon>
              </span>
              <span>{{ item.label }}</span>
            </div>
            <strong>{{ item.value }}</strong>
            <small>{{ item.hint }}</small>
          </div>
        </v-col>
      </v-row>

      <v-row dense>
        <v-col cols="12" lg="9">
          <section class="tc-panel tc-chart-panel">
            <div class="tc-panel__header">
              <div>
                <h2>{{ tituloGrafica }}</h2>
                <p>{{ subtituloGrafica }}</p>
              </div>
              <div class="tc-chart-actions">
                <v-btn-toggle
                  v-if="modoConsulta === 'day' && puntos.length"
                  v-model="vistaDia"
                  mandatory
                  dense
                  class="tc-day-toggle"
                >
                  <v-btn value="detalle">
                    <v-icon left small>mdi-chart-line</v-icon>
                    Detalle
                  </v-btn>
                  <v-btn value="hora">
                    <v-icon left small>mdi-clock-time-four-outline</v-icon>
                    Por hora
                  </v-btn>
                </v-btn-toggle>
                <v-chip small label color="cyan lighten-5" text-color="cyan darken-4">
                  {{ puntosGrafica.length }} puntos
                </v-chip>
              </div>
            </div>

            <div v-if="chartMontado && puntosGrafica.length" class="tc-chart-wrap">
              <apexchart height="460" type="line" :options="chartOptions" :series="series" />
            </div>
            <div v-else class="tc-empty">
              <v-icon size="44" color="cyan darken-2">mdi-chart-line</v-icon>
              <strong>Sin datos para graficar</strong>
              <span>{{ emptyText }}</span>
            </div>
          </section>
        </v-col>

        <v-col cols="12" lg="3">
          <section class="tc-panel tc-table-panel">
            <div class="tc-panel__header compact">
              <div>
                <h2>Lecturas</h2>
                <p>Ultimos {{ filasTabla.length }} registros</p>
              </div>
            </div>

            <v-data-table
              :headers="headersTabla"
              :items="filasTabla"
              :items-per-page="10"
              dense
              class="tc-table"
              no-data-text="Sin lecturas"
              :footer-props="{ 'items-per-page-options': [10, 25, 50] }"
            >
              <template v-slot:item.tc="{ item }">
                <span class="font-weight-medium">{{ item.tc }}</span>
              </template>
            </v-data-table>
          </section>
        </v-col>
      </v-row>
    </section>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3500" right bottom>
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">Cerrar</v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import axios from "axios";
import moment from "moment";

export default {
  name: "GraficaTiempoRealv",
  props: {
    active: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      showIntroSplash: true,
      modoConsulta: "live",
      vistaDia: "detalle",
      fechaMenu: false,
      areas: [],
      instancias: [],
      clavesDisponibles: [],
      tokenActual: null,
      loadingInstancias: false,
      loadingClaves: false,
      consultando: false,
      filtros: {
        area: null,
        instancia: null,
        fecha: moment().format("YYYY-MM-DD"),
      },
      opcionesIntervalo: [
        { label: "5 segundos", valor: 5000 },
        { label: "10 segundos", valor: 10000 },
        { label: "30 segundos", valor: 30000 },
        { label: "1 minuto", valor: 60000 },
      ],
      intervaloConsulta: 10000,
      interval: null,
      monitoreando: false,
      chartMontado: false,
      ultimaActualizacion: "Sin consultas",
      puntos: [],
      maxPuntos: 1200,
      headersTabla: [
        { text: "Hora", value: "hora", sortable: false },
        { text: "TC", value: "tc", sortable: false },
      ],
      snackbar: {
        show: false,
        text: "",
        color: "success",
      },
    };
  },
  computed: {
    puedeConsultar() {
      return Boolean(this.active && this.filtros.instancia && this.tokenActual && (this.modoConsulta === "live" || this.filtros.fecha));
    },
    estadoTexto() {
      if (this.monitoreando) return "En vivo";
      if (this.consultando) return "Consultando";
      return this.modoConsulta === "day" ? "Historico" : "En pausa";
    },
    estadoIcono() {
      if (this.monitoreando) return "mdi-broadcast";
      if (this.consultando) return "mdi-loading";
      return this.modoConsulta === "day" ? "mdi-calendar-search" : "mdi-pause-circle-outline";
    },
    estadoColor() {
      if (this.monitoreando) return "success";
      if (this.consultando) return "primary";
      return "grey lighten-2";
    },
    estadoTextoColor() {
      return this.monitoreando || this.consultando ? "white" : "grey darken-3";
    },
    instanciasFiltradas() {
      const lista = Array.isArray(this.instancias) ? this.instancias : [];
      if (!this.filtros.area) return lista;
      return lista.filter((item) => Number(item.idArea) === Number(this.filtros.area));
    },
    maquinaSeleccionada() {
      return this.instancias.find((item) => Number(item.idMaquinaIns) === Number(this.filtros.instancia));
    },
    nombreMaquina() {
      return this.maquinaSeleccionada ? this.textoMaquina(this.maquinaSeleccionada) : "Sin maquina";
    },
    tituloGrafica() {
      return this.modoConsulta === "live" ? "TC en vivo" : "TC por dia";
    },
    subtituloGrafica() {
      if (!this.tokenActual) return "Selecciona una maquina; el token TC se detecta automatico";
      const rango = this.modoConsulta === "live" ? "ultimos 5 minutos" : this.filtros.fecha;
      const vista = this.modoConsulta === "day" && this.vistaDia === "hora" ? "promedio por hora" : "detalle";
      return `${this.nombreMaquina} | ${this.tokenActual} | ${rango} | ${vista}`;
    },
    emptyText() {
      return this.modoConsulta === "live"
        ? "Inicia el modo en vivo para cargar los ultimos 5 minutos."
        : "Selecciona fecha y presiona consultar dia.";
    },
    estadisticas() {
      const valores = this.puntos.map((p) => Number(p.y)).filter((v) => Number.isFinite(v) && v > 0);
      if (!valores.length) {
        return { actual: null, promedio: null, minimo: null, maximo: null };
      }
      const suma = valores.reduce((acc, val) => acc + val, 0);
      return {
        actual: valores[valores.length - 1],
        promedio: suma / valores.length,
        minimo: Math.min(...valores),
        maximo: Math.max(...valores),
      };
    },
    tarjetas() {
      return [
        {
          label: "TC actual",
          value: this.formatearSeg(this.estadisticas.actual),
          hint: this.puntos.length ? "Ultima lectura" : "Esperando datos",
          icon: "mdi-speedometer",
          color: "#0e7490",
          soft: "rgba(14, 116, 144, 0.12)",
        },
        {
          label: "Promedio",
          value: this.formatearSeg(this.estadisticas.promedio),
          hint: "Balance del periodo",
          icon: "mdi-chart-timeline-variant",
          color: "#0891b2",
          soft: "rgba(8, 145, 178, 0.11)",
        },
        {
          label: "Minimo",
          value: this.formatearSeg(this.estadisticas.minimo),
          hint: "Mejor ciclo",
          icon: "mdi-trending-down",
          color: "#0f766e",
          soft: "rgba(15, 118, 110, 0.12)",
        },
        {
          label: "Maximo",
          value: this.formatearSeg(this.estadisticas.maximo),
          hint: "Pico detectado",
          icon: "mdi-trending-up",
          color: "#155e75",
          soft: "rgba(21, 94, 117, 0.12)",
        },
      ];
    },
    puntosPorHora() {
      const grupos = new Map();
      this.puntos.forEach((p) => {
        const hora = moment(p.x).startOf("hour").valueOf();
        const actual = grupos.get(hora) || { x: hora, suma: 0, total: 0 };
        actual.suma += Number(p.y);
        actual.total += 1;
        grupos.set(hora, actual);
      });

      return Array.from(grupos.values())
        .sort((a, b) => a.x - b.x)
        .map((g) => ({ x: g.x, y: g.total ? g.suma / g.total : 0 }));
    },
    puntosGrafica() {
      return this.modoConsulta === "day" && this.vistaDia === "hora"
        ? this.puntosPorHora
        : this.puntos;
    },
    series() {
      return [
        {
          name: this.modoConsulta === "day" && this.vistaDia === "hora" ? "Promedio por hora" : "Tiempo ciclo",
          data: this.puntosGrafica.map((p) => ({ x: p.x, y: Number(p.y.toFixed(2)) })),
        },
      ];
    },
    chartOptions() {
      return {
        chart: {
          id: "tc-postgres-premium",
          toolbar: { show: true },
          animations: { enabled: true, easing: "easeinout", speed: 350 },
          zoom: { enabled: true },
          foreColor: "#334155",
        },
        colors: ["#0e7490"],
        stroke: { curve: "smooth", width: 3 },
        markers: { size: 3, strokeWidth: 0 },
        grid: { borderColor: "#e2e8f0", strokeDashArray: 4, padding: { left: 8, right: 18 } },
        xaxis: {
          type: "datetime",
          labels: { datetimeUTC: false, format: "HH:mm" },
          title: { text: "Hora" },
        },
        yaxis: {
          min: 0,
          forceNiceScale: true,
          title: { text: "Segundos por pieza" },
          labels: { formatter: (value) => `${Number(value).toFixed(1)}s` },
        },
        tooltip: {
          theme: "light",
          x: { format: "yyyy-MM-dd HH:mm:ss" },
          y: { formatter: (value) => `${Number(value).toFixed(2)} s` },
        },
        dataLabels: { enabled: false },
      };
    },
    filasTabla() {
      return this.puntosGrafica
        .slice(-80)
        .reverse()
        .map((p) => ({
          hora: moment(p.x).format("YYYY-MM-DD HH:mm:ss"),
          tc: this.formatearSeg(p.y),
        }));
    },
  },
  watch: {
    active(value) {
      if (!value) this.detenerMonitoreo();
    },
  },
  mounted() {
    this.cargarCatalogos();
    window.setTimeout(() => {
      this.showIntroSplash = false;
    }, 950);
  },
  beforeDestroy() {
    this.detenerMonitoreo();
  },
  methods: {
    textoMaquina(item) {
      if (!item) return "";
      const marca = (item.marca || "").trim();
      const modelo = (item.modelo || "").trim();
      const nombre = (item.nombre || "").trim();
      return [marca, modelo].filter(Boolean).join(" - ") || nombre || `Maquina ${item.idMaquinaIns}`;
    },
    async cargarCatalogos() {
      this.loadingInstancias = true;
      try {
        const [areasResp, instanciasResp] = await Promise.all([
          axios.get("/Areas"),
          axios.get("/MaquinaInstancias"),
        ]);
        this.areas = Array.isArray(areasResp.data) ? areasResp.data : [];
        this.instancias = Array.isArray(instanciasResp.data) ? instanciasResp.data : [];
      } catch (error) {
        this.notificar("No se pudieron cargar los catalogos", "error");
      } finally {
        this.loadingInstancias = false;
      }
    },
    onModoChange() {
      this.detenerMonitoreo(false);
      if (this.modoConsulta !== "day") this.vistaDia = "detalle";
      this.limpiarDatos();
      this.ultimaActualizacion = this.modoConsulta === "live" ? "Listo para en vivo" : "Listo para consulta diaria";
    },
    onAreaChange() {
      this.detenerMonitoreo();
      this.filtros.instancia = null;
      this.tokenActual = null;
      this.clavesDisponibles = [];
      this.limpiarDatos();
    },
    async onMaquinaChange() {
      this.detenerMonitoreo();
      this.tokenActual = null;
      this.clavesDisponibles = [];
      this.limpiarDatos();
      if (!this.filtros.instancia) return;

      this.loadingClaves = true;
      try {
        const { data } = await axios.get("/SensorDatos/TokensPorMaquina", {
          params: { idMaquinaIns: this.filtros.instancia },
        });
        this.clavesDisponibles = Array.isArray(data) ? data.filter(Boolean) : [];
        this.tokenActual = this.clavesDisponibles[0] || null;
        if (!this.tokenActual) {
          this.notificar("La maquina no tiene token de tiempo ciclo configurado", "warning");
        }
      } catch (error) {
        this.notificar("No se pudieron cargar los tokens de la maquina", "error");
      } finally {
        this.loadingClaves = false;
      }
    },
    setIntervalo(valor) {
      this.intervaloConsulta = valor;
      if (this.monitoreando) this.iniciarPolling();
    },
    toggleMonitoreo() {
      if (this.monitoreando) {
        this.detenerMonitoreo();
        return;
      }
      this.iniciarMonitoreo();
    },
    async iniciarMonitoreo() {
      if (!this.puedeConsultar || this.modoConsulta !== "live") return;
      this.detenerMonitoreo(false);
      this.monitoreando = true;
      await this.cargarVentanaEnVivo();
      if (this.monitoreando) this.iniciarPolling();
    },
    detenerMonitoreo(mostrarMensaje = true) {
      this.monitoreando = false;
      if (this.interval) {
        clearInterval(this.interval);
        this.interval = null;
      }
      if (mostrarMensaje) this.ultimaActualizacion = "Monitoreo detenido";
    },
    iniciarPolling() {
      if (this.interval) clearInterval(this.interval);
      if (!this.monitoreando || !this.puedeConsultar || this.modoConsulta !== "live") return;
      this.interval = setInterval(() => {
        if (this.monitoreando && this.puedeConsultar && this.modoConsulta === "live") {
          this.consultarNuevosDatos();
        }
      }, this.intervaloConsulta);
    },
    async cargarVentanaEnVivo() {
      const fin = moment();
      const inicio = moment().subtract(5, "minutes");
      await this.consultarSerie(inicio, fin, true, true);
    },
    async consultarNuevosDatos() {
      const fin = moment();
      const ultimo = this.puntos.length ? this.puntos[this.puntos.length - 1].x : null;
      const inicio = ultimo ? moment(ultimo).subtract(1, "seconds") : moment().subtract(5, "minutes");
      await this.consultarSerie(inicio, fin, false, true);
    },
    async consultarDia() {
      if (!this.puedeConsultar || this.modoConsulta !== "day") return;
      this.detenerMonitoreo(false);
      const inicio = moment(this.filtros.fecha, "YYYY-MM-DD").startOf("day");
      const fin = moment(this.filtros.fecha, "YYYY-MM-DD").endOf("day");
      await this.consultarSerie(inicio, fin, true, false);
    },
    async consultarSerie(inicio, fin, reemplazar, requiereMonitoreo) {
      if (!this.puedeConsultar) return;
      if (requiereMonitoreo && (!this.monitoreando || this.modoConsulta !== "live")) return;

      this.consultando = true;
      try {
        const { data } = await axios.get("/SensorDatos/SeriesRealtimePostgres", {
          params: {
            inicio: inicio.format("YYYY-MM-DD HH:mm:ss"),
            fin: fin.format("YYYY-MM-DD HH:mm:ss"),
            idMaquinaIns: this.filtros.instancia,
            key: this.tokenActual,
          },
        });

        const serie = Array.isArray(data?.series) && data.series.length ? data.series[0] : null;
        const nuevos = serie && Array.isArray(serie.data)
          ? serie.data
              .map((p) => ({ x: Number(p.x), y: Number(p.y) }))
              .filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y) && p.y > 0)
          : [];

        this.aplicarPuntos(nuevos, reemplazar);
        this.chartMontado = true;
        this.ultimaActualizacion = moment().format("YYYY-MM-DD HH:mm:ss");
      } catch (error) {
        this.detenerMonitoreo(false);
        this.notificar("Error consultando TC en Postgres. Consulta detenida.", "error");
      } finally {
        this.consultando = false;
      }
    },
    aplicarPuntos(nuevos, reemplazar) {
      const origen = reemplazar ? [] : this.puntos.slice();
      const porTimestamp = new Map(origen.map((p) => [p.x, p]));
      nuevos.forEach((p) => porTimestamp.set(p.x, p));
      this.puntos = Array.from(porTimestamp.values())
        .sort((a, b) => a.x - b.x)
        .slice(-this.maxPuntos);
    },
    limpiarDatos() {
      this.puntos = [];
      this.chartMontado = false;
      this.ultimaActualizacion = this.monitoreando ? this.ultimaActualizacion : "Sin consultas";
    },
    formatearSeg(valor) {
      return Number.isFinite(Number(valor)) ? `${Number(valor).toFixed(2)} s` : "--";
    },
    notificar(text, color = "info") {
      this.snackbar = { show: true, text, color };
    },
  },
};
</script>

<style scoped>
.tc-page {
  background:
    radial-gradient(circle at 12% 0%, rgba(14, 116, 144, 0.12), transparent 28%),
    linear-gradient(180deg, #effcff 0%, #f8fdff 44%, #f8fafc 100%);
  color: #172033;
  min-height: 100vh;
  width: 100%;
}

.tc-shell {
  margin: 0 auto;
  max-width: none;
  width: 100%;
}

.tc-hero {
  align-items: center;
  background:
    radial-gradient(circle at 92% 0%, rgba(14, 116, 144, 0.2), transparent 34%),
    linear-gradient(135deg, #ffffff 0%, #f8fdff 50%, #ecfeff 100%);
  border: 1px solid rgba(14, 116, 144, 0.18);
  border-radius: 12px;
  box-shadow: 0 22px 52px rgba(21, 94, 117, 0.12);
  display: flex;
  gap: 16px;
  justify-content: space-between;
  margin-bottom: 18px;
  min-height: 150px;
  padding: 30px 34px;
  position: relative;
  overflow: hidden;
}

.tc-eyebrow {
  color: #0e7490;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0;
  text-transform: uppercase;
}

.tc-hero h1 {
  color: #0f172a;
  font-size: 34px;
  font-weight: 900;
  line-height: 1.1;
  margin: 4px 0 10px;
}

.tc-hero p,
.tc-panel p {
  color: #64748b;
  margin: 0;
}

.tc-hero p {
  color: #475569;
}

.tc-hero-icon {
  box-shadow: 0 16px 32px rgba(14, 116, 144, 0.24);
  flex: 0 0 auto;
  position: relative;
  z-index: 1;
}

.tc-hero-copy {
  flex: 1;
  position: relative;
  z-index: 1;
}

.tc-hero h1::after {
  background: linear-gradient(90deg, #0e7490, rgba(8, 145, 178, 0.5));
  border-radius: 999px;
  content: "";
  display: block;
  height: 3px;
  margin-top: 8px;
  width: 72px;
}

.tc-hero-sparks {
  align-items: center;
  display: flex;
  gap: 12px;
  position: relative;
  z-index: 1;
}

.tc-hero-spark {
  align-items: center;
  animation: spark-float 3.2s ease-in-out infinite;
  background: rgba(255, 255, 255, 0.78);
  border: 1px solid rgba(14, 116, 144, 0.18);
  border-radius: 14px;
  box-shadow: 0 16px 30px rgba(21, 94, 117, 0.12);
  display: inline-flex;
  height: 48px;
  justify-content: center;
  width: 48px;
}

.tc-hero-spark .v-icon {
  color: #0e7490 !important;
}

.tc-hero-spark--chart {
  animation-delay: .32s;
}

.tc-hero-spark--day {
  animation-delay: .64s;
}

.tc-control-panel,
.tc-stat,
.tc-panel {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 18px 34px rgba(15, 23, 42, 0.055);
}

.tc-control-panel {
  margin-bottom: 18px;
  padding: 18px;
}

.tc-mode-row,
.tc-action-row,
.tc-primary-actions {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.tc-mode-row {
  gap: 16px;
  justify-content: space-between;
  margin-bottom: 16px;
}

.tc-mode-toggle {
  background: linear-gradient(135deg, #ecfeff 0%, #f0fdff 100%);
  border: 1px solid rgba(14, 116, 144, 0.22);
  border-radius: 12px;
  padding: 4px;
}

.tc-mode-toggle .v-btn {
  background: transparent !important;
  border-radius: 6px !important;
  color: #334155 !important;
  font-weight: 800;
  text-transform: none;
}

.tc-mode-toggle .v-btn--active {
  background: linear-gradient(135deg, #0e7490 0%, #155e75 100%) !important;
  box-shadow: 0 10px 22px rgba(14, 116, 144, 0.24);
  color: #ffffff !important;
}

.tc-mode-toggle .v-btn--active .tc-mode-icon {
  color: #ffffff !important;
}

.tc-mode-icon--live {
  color: #0e7490 !important;
}

.tc-mode-icon--day {
  color: #0891b2 !important;
}

.tc-mode-note {
  align-items: center;
  color: #475569;
  display: flex;
  font-size: 13px;
  font-weight: 700;
  gap: 6px;
}


.tc-token-pill {
  align-items: center;
  background: #f8fdff;
  border: 1px solid rgba(14, 116, 144, 0.14);
  border-radius: 8px;
  display: flex;
  gap: 10px;
  min-height: 40px;
  padding: 0 12px;
}

.tc-field ::v-deep .v-input__slot,
.tc-date-field ::v-deep .v-input__slot {
  background: #ffffff !important;
  border-color: rgba(14, 116, 144, 0.22) !important;
  box-shadow: inset 0 0 0 1px rgba(14, 116, 144, 0.04);
}

.tc-field ::v-deep .v-icon,
.tc-date-field ::v-deep .v-icon {
  color: #0e7490 !important;
}

.tc-field ::v-deep label,
.tc-date-field ::v-deep label,
.tc-date-field ::v-deep input {
  color: #334155 !important;
}

.tc-token-pill--empty {
  background: #f1f5f9;
}

.tc-token-pill span {
  color: #64748b;
  display: block;
  font-size: 10px;
  font-weight: 900;
  line-height: 1;
  text-transform: uppercase;
}

.tc-token-pill strong {
  color: #0f172a;
  display: block;
  font-size: 13px;
  font-weight: 900;
  line-height: 1.35;
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.tc-live-window {
  background: linear-gradient(135deg, #ecfeff, #f0fdff);
  border: 1px solid rgba(14, 116, 144, 0.22);
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  height: 40px;
  justify-content: center;
  padding: 0 12px;
}

.tc-live-window span {
  color: #64748b;
  font-size: 10px;
  font-weight: 800;
  line-height: 1;
  text-transform: uppercase;
}

.tc-live-window strong {
  color: #0f172a;
  font-size: 13px;
  line-height: 1.4;
}

.tc-action-row {
  border-top: 1px solid #edf2f7;
  gap: 16px;
  justify-content: space-between;
  margin-top: 16px;
  padding-top: 14px;
}

.tc-action-btn {
  border-radius: 8px !important;
  box-shadow: 0 12px 24px rgba(14, 116, 144, 0.22) !important;
  color: #ffffff !important;
  font-weight: 900;
  text-transform: none;
}

.tc-clean-btn,
.tc-interval-btn {
  background: #f8fafc !important;
  border: 1px solid #dbe7ef;
  border-radius: 8px !important;
  color: #334155 !important;
  font-weight: 800;
  text-transform: none;
}

.tc-clean-btn .v-icon {
  color: #f97316 !important;
}

.tc-interval-btn .v-icon {
  color: #0e7490 !important;
}

.tc-date-trigger {
  align-items: center;
  background: linear-gradient(135deg, #ffffff 0%, #f8fdff 100%);
  border: 1px solid rgba(14, 116, 144, 0.22);
  border-radius: 12px;
  box-shadow: 0 12px 26px rgba(21, 94, 117, 0.08);
  color: #0f172a;
  cursor: pointer;
  display: flex;
  gap: 10px;
  height: 48px;
  padding: 0 10px;
  text-align: left;
  width: 100%;
}

.tc-date-trigger__icon {
  align-items: center;
  background: linear-gradient(135deg, #0e7490, #155e75);
  border-radius: 10px;
  display: inline-flex;
  height: 34px;
  justify-content: center;
  width: 34px;
}

.tc-date-trigger__icon .v-icon {
  color: #ffffff !important;
  font-size: 19px !important;
}

.tc-date-trigger__copy {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
}

.tc-date-trigger__copy span {
  color: #0e7490;
  font-size: 10px;
  font-weight: 900;
  line-height: 1;
  text-transform: uppercase;
}

.tc-date-trigger__copy strong {
  color: #0f172a;
  font-size: 13px;
  font-weight: 900;
  line-height: 1.35;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tc-date-trigger__chevron {
  color: #0e7490 !important;
}

.tc-stat {
  background:
    linear-gradient(180deg, rgba(255,255,255,.98) 0%, rgba(248,253,255,.98) 100%),
    var(--stat-soft);
  border: 1px solid rgba(148, 163, 184, 0.16);
  border-radius: 12px;
  box-shadow: 0 18px 34px rgba(15, 23, 42, 0.06);
  display: flex;
  flex-direction: column;
  gap: 9px;
  min-height: 132px;
  overflow: hidden;
  padding: 18px;
  position: relative;
}

.tc-stat__glow {
  background: radial-gradient(circle, var(--stat-soft) 0%, transparent 68%);
  height: 126px;
  position: absolute;
  right: -46px;
  top: -50px;
  width: 126px;
}

.tc-stat__top {
  align-items: center;
  display: flex;
  gap: 10px;
  position: relative;
  z-index: 1;
}

.tc-stat__icon {
  align-items: center;
  background: var(--stat-soft);
  border: 1px solid rgba(14, 116, 144, 0.16);
  border-radius: 12px;
  display: inline-flex;
  height: 42px;
  justify-content: center;
  width: 42px;
}

.tc-stat__icon .v-icon {
  color: var(--stat-color) !important;
}

.tc-stat span {
  color: #64748b;
  font-size: 12px;
  font-weight: 800;
  text-transform: uppercase;
}

.tc-stat strong {
  color: #0f172a;
  font-size: 28px;
  font-weight: 900;
  line-height: 1;
  position: relative;
  z-index: 1;
}

.tc-stat small {
  color: #64748b;
  font-size: 11px;
  font-weight: 800;
  position: relative;
  text-transform: uppercase;
  z-index: 1;
}

.tc-panel {
  min-height: 562px;
  overflow: hidden;
}

.tc-panel__header {
  align-items: flex-start;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  padding: 18px 20px;
}

.tc-chart-actions {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: flex-end;
}

.tc-day-toggle {
  background: #ecfeff;
  border: 1px solid rgba(14, 116, 144, 0.18);
  border-radius: 10px;
  padding: 3px;
}

.tc-day-toggle .v-btn {
  background: transparent !important;
  border-radius: 7px !important;
  color: #475569 !important;
  font-weight: 900;
  letter-spacing: 0;
  text-transform: none;
}

.tc-day-toggle .v-btn .v-icon {
  color: #0e7490 !important;
}

.tc-day-toggle .v-btn--active {
  background: #ffffff !important;
  box-shadow: 0 8px 20px rgba(21, 94, 117, 0.12);
  color: #155e75 !important;
}

.tc-panel__header.compact {
  min-height: 82px;
}

.tc-panel h2 {
  color: #0f172a;
  font-size: 19px;
  font-weight: 900;
  margin: 0 0 4px;
}

.tc-chart-wrap {
  padding: 16px 12px 8px;
}

.tc-empty {
  align-items: center;
  color: #64748b;
  display: flex;
  flex-direction: column;
  gap: 8px;
  height: 460px;
  justify-content: center;
  text-align: center;
}

.tc-intro-overlay {
  align-items: center;
  background:
    radial-gradient(circle at center, rgba(14, 116, 144, 0.16), transparent 34%),
    rgba(245, 247, 251, 0.88);
  bottom: 0;
  display: flex;
  justify-content: center;
  left: 0;
  overflow: hidden;
  position: fixed;
  right: 0;
  top: 0;
  z-index: 2200;
}

.tc-intro-icon-wrap {
  position: relative;
  z-index: 3;
}

.tc-intro-icon {
  animation: intro-icon-pop .74s cubic-bezier(.2, .8, .2, 1) both;
  box-shadow: 0 24px 55px rgba(14, 116, 144, 0.26);
}

.tc-intro-ring,
.tc-intro-burst {
  border-radius: 999px;
  position: absolute;
}

.tc-intro-ring {
  border: 2px solid rgba(14, 116, 144, 0.18);
  height: 168px;
  width: 168px;
}

.tc-intro-ring--outer {
  animation: intro-ring-expand .9s ease-out both;
}

.tc-intro-ring--inner {
  animation: intro-ring-expand .75s .08s ease-out both;
  height: 122px;
  width: 122px;
}

.tc-intro-burst {
  animation: intro-burst-expand .78s ease-out both;
  background: rgba(8, 145, 178, 0.13);
  height: 230px;
  width: 230px;
}

.monitor-intro-enter-active,
.monitor-intro-leave-active {
  transition: opacity .3s ease;
}

.monitor-intro-enter,
.monitor-intro-leave-to {
  opacity: 0;
}

@keyframes intro-icon-pop {
  0% {
    opacity: 0;
    transform: scale(.3) rotate(-10deg);
  }
  68% {
    opacity: 1;
    transform: scale(1.08) rotate(2deg);
  }
  100% {
    opacity: 1;
    transform: scale(1) rotate(0);
  }
}

@keyframes intro-ring-expand {
  0% {
    opacity: 0;
    transform: scale(.35);
  }
  72% {
    opacity: 1;
  }
  100% {
    opacity: 0;
    transform: scale(1.42);
  }
}

@keyframes intro-burst-expand {
  0% {
    opacity: 0;
    transform: scale(.25);
  }
  60% {
    opacity: .95;
  }
  100% {
    opacity: 0;
    transform: scale(1.2);
  }
}

@keyframes spark-float {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-7px);
  }
}

.tc-empty strong {
  color: #1e293b;
  font-size: 18px;
}

.tc-table {
  border-radius: 0;
}

@media (max-width: 960px) {
  .tc-page {
    padding: 16px !important;
  }

  .tc-hero,
  .tc-mode-row,
  .tc-action-row {
    align-items: flex-start;
    flex-direction: column;
  }

  .tc-hero-icon {
    box-shadow: 0 14px 28px rgba(14, 116, 144, 0.18);
    flex: 0 0 auto;
  }

  .tc-hero-copy {
    flex: 1;
  }

  .tc-hero h1::after {
    background: linear-gradient(90deg, #0e7490, rgba(8, 145, 178, 0.5));
    border-radius: 999px;
    content: "";
    display: block;
    height: 3px;
    margin-top: 8px;
    width: 72px;
  }

  .tc-hero-sparks {
    justify-content: flex-start;
    width: 100%;
  }

  .tc-chart-actions {
    justify-content: flex-start;
    width: 100%;
  }

  .tc-day-toggle {
    width: 100%;
  }

  .tc-day-toggle .v-btn {
    flex: 1 1 0;
  }
}
</style>

<style>
.tc-date-menu {
  border: 1px solid rgba(14, 116, 144, 0.18);
  border-radius: 18px !important;
  box-shadow: 0 26px 54px rgba(21, 94, 117, 0.2) !important;
  overflow: hidden;
}

.tc-date-menu .v-picker {
  background: linear-gradient(180deg, #ffffff 0%, #f8fdff 100%) !important;
  border-radius: 18px !important;
}

.tc-date-menu .v-picker__body {
  background: linear-gradient(180deg, #ffffff 0%, #f8fdff 100%) !important;
}

.tc-date-menu .v-date-picker-header {
  background: #ffffff;
  border-bottom: 1px solid rgba(14, 116, 144, 0.12);
  padding: 10px 12px;
}

.tc-date-menu .v-date-picker-table {
  padding: 8px 10px 14px;
}

.tc-date-menu .v-date-picker-table th {
  color: #0e7490 !important;
  font-size: 11px;
  font-weight: 900;
}

.tc-date-menu .v-date-picker-table .v-btn:not(.v-btn--active):not(.v-btn--disabled) .v-btn__content {
  color: #0f172a !important;
  font-weight: 800;
}

.tc-date-menu .v-date-picker-table .v-btn--disabled .v-btn__content {
  color: #cbd5e1 !important;
}

.tc-date-menu .v-date-picker-table .v-btn {
  border-radius: 10px !important;
}

.tc-date-menu .v-date-picker-table .v-btn:not(.v-btn--active):not(.v-btn--disabled):hover {
  background: rgba(14, 116, 144, 0.08) !important;
}

.tc-date-menu .v-date-picker-table .v-btn--active {
  background: linear-gradient(135deg, #0e7490 0%, #155e75 100%) !important;
  box-shadow: 0 10px 20px rgba(14, 116, 144, 0.28);
}

.tc-date-menu .v-date-picker-table .v-btn--active::before {
  opacity: 0 !important;
}

.tc-date-menu .v-date-picker-table .v-btn--active .v-btn__content {
  color: #ffffff !important;
  font-weight: 900;
}

.tc-date-menu .v-date-picker-header .v-btn .v-icon,
.tc-date-menu .v-date-picker-header__value button {
  color: #164e63 !important;
  font-weight: 900;
}
</style>






